import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { CreditCard, Filter, Bell, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STATUS_CHIP = {
  paid: "bnp-chip-teal",
  unpaid: "bnp-chip-warn",
  no_payment_required: "bnp-chip-light",
  initiated: "bnp-chip-light",
};

const STATUS_FILTERS = [
  { value: "", label: "All transactions" },
  { value: "paid", label: "Paid (completed)" },
  { value: "unpaid", label: "Unpaid (abandoned / processing)" },
  { value: "initiated", label: "Checkout initiated only" },
];

export default function AdminTransactions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [webhook, setWebhook] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/admin/transactions").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
    api.get("/webhook/stripe/status").then((r) => setWebhook(r.data)).catch(() => {});
  }, []);

  const sendTestAlerts = async () => {
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/test-recurring-email");
      const to = (data.recipients || []).join(", ");
      if (data.ok) {
        toast.success(`Test alerts sent to ${to}`);
        setResult({ ok: true, msg: `Sent "Payment received" + "Payment FAILED" to ${to}. Check inboxes (including spam).` });
      } else {
        toast.error(data.detail || "Could not send test alerts");
        setResult({ ok: false, msg: data.detail || "Could not send test alerts." });
      }
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Could not send test alerts.";
      toast.error(msg);
      setResult({ ok: false, msg });
    } finally {
      setSending(false);
    }
  };

  const filtered = items.filter((i) => !statusFilter || i.payment_status === statusFilter || i.status === statusFilter);

  const totalPaid = items
    .filter((i) => i.payment_status === "paid")
    .reduce((sum, i) => sum + (i.amount || 0), 0);

  return (
    <div className="p-6 md:p-10" data-testid="admin-transactions-page">
      <div>
        <span className="bnp-chip bnp-chip-light">Transactions</span>
        <h1 className="font-display text-3xl md:text-4xl text-[#04273d] mt-2 font-semibold tracking-tight">Stripe transactions</h1>
        <p className="text-sm text-[#447694] mt-1">{items.length} sessions · £{totalPaid.toFixed(2)} captured (paid).</p>
      </div>

      <div className="bnp-card p-6 mt-6" data-testid="admin-payment-alerts-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h2 className="font-display text-lg font-semibold text-[#04273d] flex items-center gap-2">
              <Bell size={16} className="text-[#447694]" /> Payment alert emails
            </h2>
            <p className="text-sm text-[#447694] mt-1 max-w-xl">
              Sophie &amp; info@ are alerted automatically on every subscription renewal and failed payment. Send a test to confirm delivery works in this environment.
            </p>
            {webhook && (
              <p className="font-mono-brand text-[11px] text-[#447694] mt-2 tracking-wide" data-testid="admin-webhook-health">
                Webhook: {webhook.webhook_secret_configured ? "secret OK" : "NO SECRET"} · {webhook.success?.count || 0} processed · {webhook.signature_failure?.count || 0} signature failures
                {webhook.success?.last_type ? ` · last: ${webhook.success.last_type}` : ""}
              </p>
            )}
          </div>
          <button
            onClick={sendTestAlerts}
            disabled={sending}
            className="btn-primary text-sm shrink-0 disabled:opacity-60"
            data-testid="admin-send-test-alert-btn"
          >
            {sending ? "Sending…" : <>Send test alerts <ArrowRight size={14} /></>}
          </button>
        </div>
        {result && (
          <div
            className={`mt-4 text-sm rounded-md px-4 py-3 ${result.ok ? "bg-[#71edc9]/15 text-[#0a4d3b]" : "bg-amber-50 text-amber-800"}`}
            data-testid="admin-test-alert-result"
          >
            {result.msg}
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Filter size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#447694] pointer-events-none" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bnp-input pl-10 pr-8 appearance-none cursor-pointer min-w-[260px]"
            data-testid="admin-transactions-status-filter"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <p className="text-xs text-[#447694]">
          Showing <strong className="text-[#04273d]">{filtered.length}</strong> of {items.length}
        </p>
      </div>

      <div className="bnp-card mt-5 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#447694] text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#447694] text-sm flex flex-col items-center gap-2">
            <CreditCard size={24} className="text-[#9ac7f7]" />
            No transactions {statusFilter ? `at "${STATUS_FILTERS.find((s) => s.value === statusFilter)?.label}"` : "yet"}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-transactions-table">
              <thead className="bg-[#f7f7f7] text-[#447694] text-left">
                <tr>
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Plan</th>
                  <th className="px-5 py-3 font-medium">Amount</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Session</th>
                  <th className="px-5 py-3 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="border-t border-[#9ac7f7]/20">
                    <td className="px-5 py-3 text-[#04273d]">{t.email || ","}</td>
                    <td className="px-5 py-3 text-[#447694]">{t.plan_label || t.plan_id || ","}</td>
                    <td className="px-5 py-3 text-[#04273d] font-mono-brand">
                      £{(t.amount || 0).toFixed(2)} {(t.currency || "").toUpperCase()}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`bnp-chip text-[10px] ${STATUS_CHIP[t.payment_status] || "bnp-chip-light"}`}>
                        {t.payment_status || t.status || ","}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono-brand text-xs text-[#447694] max-w-[180px] truncate">{t.session_id}</td>
                    <td className="px-5 py-3 text-[#447694] font-mono-brand text-xs">
                      {t.created_at ? new Date(t.created_at).toLocaleString("en-GB") : ","}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
